'use client'

import { useMemo } from 'react'
import { Tabs, Typography } from 'src/components/design-system'
import ListLayout from 'src/components/list/ListLayout'
import ListSortControls from 'src/components/list/ListSortControls'
import PagedTileGrid from 'src/components/list/PagedTileGrid'
import { CustomInput } from 'src/components/design-system/form/CustomInput'
import { CustomSelect } from 'src/components/design-system/form/CustomSelect'
import FilterCheckboxOption from 'src/components/list/filters/FilterCheckboxOption'
import { usePagedRemoteList } from 'src/components/list/hooks/usePagedRemoteList'
import { createJournalIssuesListDefinition } from './helpers/journalIssuesListDefinition'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import {
  buildNumberVolumeOptions,
  buildVolumeMap,
  buildReleaseYearOptions,
  mapIssuesToTiles,
} from './helpers/journalIssuesListUtils'
import { formatResultsFound } from 'src/_helpers/plural'

export default function JournalIssuesListClient({
  items = [],
  totalItems: initialTotalItems = 0,
  issuesForFilter = [],
  volumes = [],
  journalSlug = '',
  journalUuid,
  locale = 'pl',
  routeContext = 'main',
  initialActiveTabId = null,
  initialSortValue = null,
  initialPage = null,
}) {
  const translateEntry = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const listDefinition = useMemo(() => createJournalIssuesListDefinition({ journalUuid }), [journalUuid])

  const volumeByUuid = useMemo(() => buildVolumeMap(volumes), [volumes])

  const numberVolumeOptions = useMemo(
    () =>
      buildNumberVolumeOptions({
        issues: issuesForFilter,
        volumes,
        locale,
        issuePrefix: translateEntry('T_ISSUE_PREFIX'),
        volumePrefix: translateEntry('T_VOLUME_PREFIX'),
        untitledLabel: translateEntry('T_UNTITLED'),
      }),
    [issuesForFilter, locale, translateEntry, volumes],
  )

  const releaseYearOptions = useMemo(() => buildReleaseYearOptions(issuesForFilter), [issuesForFilter])

  const tabs = useMemo(() => {
    return listDefinition.tabs.map((tab) => {
      if (tab.id === 'latest') {
        return { ...tab, label: translateEntry('T_ISSUES_TAB_LATEST') }
      }
      if (tab.id === 'archived') {
        return { ...tab, label: translateEntry('T_ISSUES_TAB_ARCHIVED') }
      }
      return tab
    })
  }, [listDefinition.tabs, translateEntry])

  const sortOptions = useMemo(() => {
    return listDefinition.sortOptions.map((option) => {
      if (option.value.endsWith('desc')) {
        return { ...option, label: translateEntry('T_SORT_FROM_NEWEST') }
      }
      if (option.value.endsWith('asc')) {
        return { ...option, label: translateEntry('T_SORT_FROM_OLDEST') }
      }
      return option
    })
  }, [listDefinition.sortOptions, translateEntry])

  const mapItems = useMemo(() => {
    return (rawItems, nextLocale) =>
      mapIssuesToTiles(rawItems, {
        locale: nextLocale,
        journalSlug,
        routeContext,
        journalRoutes,
        volumeByUuid,
        issuePrefix: translateEntry('T_ISSUE_PREFIX'),
        labels: {
          volumePrefix: translateEntry('T_VOLUME_PREFIX'),
          currentIssue: translateEntry('T_CURRENT_ISSUE'),
          specialIssue: translateEntry('T_SPECIAL_NUMBER'),
          openAccess: translateEntry('T_OPEN_ACCESS'),
          untitled: translateEntry('T_UNTITLED'),
        },
      })
  }, [journalSlug, routeContext, journalRoutes, translateEntry, volumeByUuid])

  const {
    activeTab,
    sort,
    page,
    totalItems,
    totalPages,
    items: tiles,
    isLoading,
    textValues,
    singleValues,
    booleanValues,
    handleTabChange,
    handleSortValueChange,
    handlePageChange,
    setTextValue,
    setSingleValue,
    toggleBooleanValue,
  } = usePagedRemoteList({
    ...listDefinition,
    sortOptions,
    tabs,
    locale,
    initialItems: items,
    initialTotalItems,
    initialTabId: tabs?.[0]?.id,
    initialActiveTabId,
    initialSortValue,
    initialPage,
    mapItems,
  })

  const resultsLabel = useMemo(
    () => formatResultsFound(totalItems, locale, translateEntry),
    [totalItems, locale, translateEntry],
  )

  const labels = {
    filters: translateEntry('T_FILTERS'),
    tabs: translateEntry('T_ISSUES_TABS'),
    numberVolume: translateEntry('T_ISSUE_VOLUME'),
    releaseYear: translateEntry('T_RELEASE_YEAR'),
    from: translateEntry('T_FILTER_FROM'),
    to: translateEntry('T_FILTER_TO'),
    doi: translateEntry('T_DOI_NUMBER'),
    onlySpecial: translateEntry('T_ONLY_SPECIAL_ISSUES'),
    results: resultsLabel,
  }
  const domainFilterColor = routeContext === 'journal-domain' ? 'journalDomain' : 'white'

  return (
    <div className="d:my-64 my-24">
      {tabs.length > 0 && (
        <Tabs
          className="after:content-[''] after:h-1 after:w-full after:bg-basic-black after:-mt-11"
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          color={routeContext === 'journal-domain' ? 'journalDomain' : 'dark'}
          ariaLabel={labels.tabs}
        />
      )}

      <ListLayout>
        <div
          className={`${routeContext === 'journal-domain' ? 'border-b-basic-gray7' : 'border-b-dark-blue-dark-blue6'} border-b pb-24`}
        >
          <Typography variant="h3-bold" component="h2" className="mb-16">
            {labels.filters}
          </Typography>

          <div className="grid gap-24 d:grid-cols-4">
            <CustomSelect
              name="numberVolume"
              label={labels.numberVolume}
              options={numberVolumeOptions}
              defaultValue={singleValues?.numberVolume || ''}
              onChange={(event) => setSingleValue('numberVolume', event?.target?.value || '')}
              color={domainFilterColor}
              classNameContainer="d:col-span-1"
            />

            <div className="d:col-span-2 grid gap-24 d:grid-cols-2">
              <CustomSelect
                name="releaseYearFrom"
                label={`${labels.releaseYear} ${labels.from}`}
                options={releaseYearOptions}
                defaultValue={singleValues?.releaseYearFrom || ''}
                onChange={(event) => setSingleValue('releaseYearFrom', event?.target?.value || '')}
                color={domainFilterColor}
              />

              <CustomSelect
                name="releaseYearTo"
                label={`${labels.releaseYear} ${labels.to}`}
                options={releaseYearOptions}
                defaultValue={singleValues?.releaseYearTo || ''}
                onChange={(event) => setSingleValue('releaseYearTo', event?.target?.value || '')}
                color={domainFilterColor}
              />
            </div>

            <CustomInput
              name="doi"
              label={labels.doi}
              defaultValue={textValues?.doi || ''}
              onChange={(event) => setTextValue('doi', event?.target?.value || '')}
              color={domainFilterColor}
              classNameContainer="d:col-span-1"
            />
          </div>

          <FilterCheckboxOption
            checked={Boolean(booleanValues?.special)}
            onChange={() => toggleBooleanValue('special')}
            label={labels.onlySpecial}
            className="mt-16 flex items-center gap-8"
          />
        </div>

        <ListSortControls
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          sortOptions={sortOptions}
          sortValue={sort?.value}
          onSortValueChange={handleSortValueChange}
          dividerClassName={
            routeContext === 'journal-domain' ? 'hidden h-32 border-l border-basic-gray7 d:block' : undefined
          }
          leading={
            <Typography variant="body3-normal" color="text-basic-gray2">
              {labels.results}
            </Typography>
          }
        />

        <PagedTileGrid
          tiles={tiles}
          isLoading={isLoading}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          desktopColumns={4}
          tileClassName={routeContext === 'journal-domain' ? 'product-tile--journal-domain' : ''}
        />
      </ListLayout>
    </div>
  )
}
